import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  autoSignIn,
  confirmSignIn,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import type { UserRole } from '@shopgnd/shared';
import type { AuthUser } from '@/stores/authStore';

function mapGroupsToRoles(groups: string[] | undefined): UserRole[] {
  const allowed: UserRole[] = ['buyer', 'vendor', 'service_provider', 'admin', 'platform_admin'];
  const set = new Set<UserRole>();
  for (const g of groups ?? []) {
    if (allowed.includes(g as UserRole)) set.add(g as UserRole);
  }
  if (groups?.includes('platform_admin') && !set.has('admin')) {
    set.add('admin');
  }
  if (set.size === 0) set.add('buyer');
  return [...set];
}

function displayNameFromPayload(
  payload: Record<string, unknown> | undefined,
  fallback: string,
): string {
  const preferred = payload?.preferred_username;
  if (typeof preferred === 'string' && preferred.trim()) return preferred.trim();
  const name = payload?.name;
  if (typeof name === 'string' && name.trim()) return name.trim();
  return fallback;
}

export async function refreshAuthUser(options?: { forceRefresh?: boolean }): Promise<AuthUser | null> {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession({ forceRefresh: options?.forceRefresh ?? false });
    const payload = (session.tokens?.idToken?.payload ??
      session.tokens?.accessToken?.payload) as Record<string, unknown> | undefined;
    const groups = (payload?.['cognito:groups'] as string[] | undefined) ?? [];
    const email =
      (user.signInDetails?.loginId as string | undefined) ??
      (typeof payload?.email === 'string' ? payload.email : user.username);
    return {
      id: user.userId,
      email,
      displayName: displayNameFromPayload(payload, email),
      roles: mapGroupsToRoles(groups),
    };
  } catch {
    return null;
  }
}

function isAlreadyAuthenticatedError(e: unknown): boolean {
  const name = e && typeof e === 'object' && 'name' in e ? String((e as { name: string }).name) : '';
  const message = e instanceof Error ? e.message : '';
  return (
    name === 'UserAlreadyAuthenticatedException' ||
    message.includes('already a signed in user')
  );
}

function emailsMatch(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

async function performEmailSignIn(email: string, password: string) {
  const result = await signIn({ username: email, password });
  if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    return { needsNewPassword: true as const };
  }
  if (!result.isSignedIn) {
    throw new Error('Sign-in could not be completed.');
  }
  return { needsNewPassword: false as const, user: await refreshAuthUser() };
}

/**
 * Signs in with email/password. If another Cognito session is already active
 * (common after creating/confirming a new user), signs that session out and
 * retries so account switching works without a manual sign-out first.
 */
export async function emailSignIn(email: string, password: string) {
  try {
    return await performEmailSignIn(email, password);
  } catch (e) {
    if (!isAlreadyAuthenticatedError(e)) throw e;

    const current = await refreshAuthUser();
    if (current && emailsMatch(current.email, email)) {
      return { needsNewPassword: false as const, user: current };
    }

    // Different account (or unknown session) — clear Cognito tokens and sign in as requested.
    await signOut();
    const { resetDataClient } = await import('@/lib/amplify-client');
    resetDataClient();
    const { useAuthStore } = await import('@/stores/authStore');
    useAuthStore.setState({ user: null });
    return await performEmailSignIn(email, password);
  }
}

export async function completeNewPasswordSignIn(newPassword: string) {
  const result = await confirmSignIn({ challengeResponse: newPassword });
  if (!result.isSignedIn) {
    throw new Error('Could not set your new password.');
  }
  return refreshAuthUser();
}

export function rolesIncludeAdmin(roles: UserRole[]): boolean {
  return roles.includes('admin') || roles.includes('platform_admin');
}

/** True when the current Cognito session is in the admin group. */
export async function callerIsShopAdmin(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    const payload = (session.tokens?.idToken?.payload ??
      session.tokens?.accessToken?.payload) as Record<string, unknown> | undefined;
    const groups = (payload?.['cognito:groups'] as string[] | undefined) ?? [];
    return groups.includes('admin') || groups.includes('platform_admin');
  } catch {
    return false;
  }
}

export function defaultPathForRoles(roles: UserRole[]): string {
  if (
    roles.some(
      (r) => r === 'platform_admin' || r === 'admin' || r === 'vendor' || r === 'service_provider',
    )
  ) {
    return '/hub';
  }
  return '/';
}

export async function emailSignUp(
  email: string,
  password: string,
  displayName: string,
) {
  const { platformEmailAllowedForPublicSignup, platformEmailBlockedMessage } = await import(
    '@/lib/platformEmailPolicy'
  );
  if (!platformEmailAllowedForPublicSignup(email)) {
    throw new Error(platformEmailBlockedMessage());
  }

  // /join is reachable while signed in; clear any existing session so confirm/autoSignIn
  // does not leave Cognito stuck on the previous user (e.g. admin → new buyer).
  try {
    await getCurrentUser();
    await signOut();
    const { resetDataClient } = await import('@/lib/amplify-client');
    resetDataClient();
    const { useAuthStore } = await import('@/stores/authStore');
    useAuthStore.setState({ user: null });
  } catch {
    /* not signed in */
  }

  await signUp({
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        preferred_username: displayName,
        name: displayName,
      },
      autoSignIn: true,
    },
  });
}

function isAlreadyConfirmedError(e: unknown): boolean {
  const name =
    e && typeof e === 'object' && 'name' in e ? String((e as { name: string }).name) : '';
  const message = e instanceof Error ? e.message : String(e);
  if (/already confirmed/i.test(message)) return true;
  return (
    name === 'NotAuthorizedException' &&
    /cannot be confirm(ed)?/i.test(message) &&
    /CONFIRMED/i.test(message)
  );
}

async function tryAutoSignInAfterSignUp(): Promise<AuthUser | null> {
  try {
    const signInResult = await autoSignIn();
    if (signInResult.isSignedIn) {
      return await refreshAuthUser({ forceRefresh: true });
    }
  } catch {
    /* no pending auto sign-in */
  }
  return null;
}

/** Confirms signup, then signs the user in (autoSignIn or password fallback). */
export async function confirmEmailSignUp(
  email: string,
  code: string,
  password?: string,
): Promise<AuthUser> {
  let alreadyConfirmed = false;
  try {
    const { nextStep, isSignUpComplete } = await confirmSignUp({
      username: email,
      confirmationCode: code,
    });

    if (nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
      const fromAuto = await tryAutoSignInAfterSignUp();
      if (fromAuto) return fromAuto;
    }

    if (password) {
      const result = await emailSignIn(email, password);
      if (result.user) return result.user;
      if (result.needsNewPassword) {
        throw new Error('Your account needs a new password. Use the sign-in page to continue.');
      }
    }

    if (isSignUpComplete || nextStep.signUpStep === 'DONE') {
      const fromAuto = await tryAutoSignInAfterSignUp();
      if (fromAuto) return fromAuto;

      const user = await refreshAuthUser({ forceRefresh: true });
      if (user) return user;
    }
  } catch (e) {
    if (isAlreadyConfirmedError(e)) {
      alreadyConfirmed = true;
    } else {
      throw e;
    }
  }

  if (password) {
    const result = await emailSignIn(email, password);
    if (result.user) return result.user;
    if (result.needsNewPassword) {
      throw new Error('Your account needs a new password. Use the sign-in page to continue.');
    }
  }

  if (alreadyConfirmed) {
    const fromAuto = await tryAutoSignInAfterSignUp();
    if (fromAuto) return fromAuto;
    const user = await refreshAuthUser({ forceRefresh: true });
    if (user) return user;
  }

  throw new Error('Email confirmed. Sign in with your email and password to continue.');
}

export async function resendEmailConfirmation(email: string): Promise<'sent' | 'alreadyConfirmed'> {
  try {
    await resendSignUpCode({ username: email });
    return 'sent';
  } catch (e) {
    if (isAlreadyConfirmedError(e)) return 'alreadyConfirmed';
    throw e;
  }
}

export async function requestPasswordReset(email: string) {
  await resetPassword({ username: email });
}

export async function completePasswordReset(
  email: string,
  code: string,
  newPassword: string,
) {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}

export async function appSignOut() {
  await signOut();
  const { resetDataClient } = await import('@/lib/amplify-client');
  const { notifyCatalogRefresh } = await import('@/lib/catalog-refresh');
  resetDataClient();
  notifyCatalogRefresh();
}

export async function getIdToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: options?.forceRefresh ?? false });
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}
